import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaBriefcase,
  FaCalendarAlt,
  FaEnvelope,
  FaInfoCircle,
  FaNewspaper,
  FaPenNib,
  FaShieldAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaUsersCog,
} from "react-icons/fa";

import CustomNavbar from "../components/CustomNavbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

const initials = (name = "") =>
  name
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "U";

const ADMIN_TOOLS = [
  {
    to: "/notes",
    Icon: FaStickyNote,
    title: "Sticky Notes",
    desc: "Create, pin, tag and manage the team board.",
  },
  {
    to: "/admin/blog",
    Icon: FaPenNib,
    title: "Create Blog",
    desc: "Write and publish a new blog post.",
  },
  {
    to: "/admin/users",
    Icon: FaUsersCog,
    title: "Manage Users",
    desc: "Promote members to admin or revoke access.",
  },
];

const MEMBER_TOOLS = [
  {
    to: "/blog",
    Icon: FaNewspaper,
    title: "Read Our Blog",
    desc: "Explore the latest articles and insights.",
  },
  {
    to: "/services",
    Icon: FaBriefcase,
    title: "Our Services",
    desc: "Browse what Sarvavya can do for you.",
  },
  {
    to: "/contact",
    Icon: FaEnvelope,
    title: "Contact Us",
    desc: "Get in touch with our team.",
  },
];

function ToolCard({ to, Icon, title, desc }) {
  return (
    <Link to={to} className="acc-tool" data-live>
      <span className="acc-tool__icon">
        <Icon />
      </span>
      <h3 className="acc-tool__title">{title}</h3>
      <p className="acc-tool__desc">{desc}</p>
      <span className="acc-tool__more">
        Open <FaArrowRight />
      </span>
    </Link>
  );
}

function Account() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const tools = isAdmin ? ADMIN_TOOLS : MEMBER_TOOLS;

  return (
    <>
      <CustomNavbar />

      <style>{`
        .acc {
          --peach-deep: #ff6b35;
          --peach: #ff9066;
          --peach-soft: #f3c09f;
          --peach-tint: #ffe9e0;
          --peach-ink: #c2410c;
        }

        /* Navy rather than the peach used on marketing pages — this is the
           signed-in area, and the darker header sets it apart. */
        .acc-head {
          position: relative; overflow: hidden;
          padding: clamp(44px, 6vw, 70px) 0;
          color: #fff;
          background:
            radial-gradient(36rem 20rem at 86% 4%, rgba(255,107,53,.30), transparent 62%),
            linear-gradient(150deg, #131f36 0%, #0f172a 55%, #0b1120 100%);
        }
        .acc-head__mesh {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(255,144,102,.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,144,102,.07) 1px, transparent 1px);
          background-size: 64px 64px;
          mask-image: radial-gradient(70% 60% at 40% 45%, #000 20%, transparent 100%);
          -webkit-mask-image: radial-gradient(70% 60% at 40% 45%, #000 20%, transparent 100%);
        }
        .acc-profile { display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
        .acc-avatar {
          width: 92px; height: 92px; border-radius: 26px; flex-shrink: 0;
          display: grid; place-items: center;
          font-size: 2rem; font-weight: 800; color: #0f172a;
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          box-shadow: 0 16px 34px rgba(255,107,53,.42);
        }
        .acc-name {
          margin: 0; font-weight: 800; letter-spacing: -.02em; color: #fff;
          font-size: clamp(1.5rem, 3.4vw, 2.1rem);
        }
        .acc-email { color: #cbd5e1; margin: 6px 0 0; font-size: .96rem; }
        .acc-chips { display: flex; flex-wrap: wrap; gap: 9px; margin-top: 12px; }
        .acc-chip {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 6px 14px; border-radius: 999px;
          font-size: .76rem; font-weight: 700; letter-spacing: .04em;
        }
        .acc-chip--role {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach)); color: #0f172a;
        }
        .acc-chip--meta {
          background: rgba(255,255,255,.07); color: #cbd5e1;
          border: 1px solid rgba(255,144,102,.26);
        }
        .acc-chip--meta svg { color: var(--peach); }

        /* Bottom padding leaves room for the footer's overlapping CTA panel */
        .acc-body { padding: clamp(38px, 5vw, 56px) 0 clamp(92px, 10vw, 130px); background: #fff; }
        .acc-bar {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 14px; margin-bottom: 26px;
        }
        .acc-h2 {
          margin: 0; color: #0f172a; font-weight: 800;
          font-size: clamp(1.25rem, 2.4vw, 1.6rem); letter-spacing: -.01em;
        }
        .acc-logout {
          display: inline-flex; align-items: center; gap: 9px;
          background: #fff; color: var(--peach-ink);
          border: 1.5px solid #ffd4c2; border-radius: 12px;
          padding: 10px 20px; font-weight: 700; font-size: .9rem;
          transition: background .2s ease, color .2s ease,
                      border-color .2s ease, transform .2s ease;
        }
        .acc-logout:hover, .acc-logout:focus-visible {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #0f172a; border-color: transparent; transform: translateY(-2px);
        }

        .acc-tool {
          position: relative; overflow: hidden; height: 100%;
          display: flex; flex-direction: column;
          padding: 26px 24px; border-radius: 20px; text-decoration: none;
          background: #fff; border: 1px solid #f0e4dd;
          box-shadow: 0 6px 22px rgba(194,65,12,.07);
          transition: transform .26s ease, box-shadow .26s ease, border-color .26s ease;
        }
        .acc-tool::before {
          content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, var(--peach-deep), var(--peach-soft));
          transform: scaleX(0); transform-origin: left;
          transition: transform .34s cubic-bezier(.22,1,.36,1);
        }
        .acc-tool:focus-visible { outline: 3px solid var(--peach-deep); outline-offset: 3px; }
        .acc-tool__icon {
          width: 56px; height: 56px; border-radius: 16px; margin-bottom: 16px;
          display: grid; place-items: center; font-size: 1.2rem;
          background: var(--peach-tint); color: var(--peach-deep);
          transition: background .26s ease, color .26s ease, transform .26s ease;
        }
        .acc-tool__title { color: #0f172a; font-weight: 700; font-size: 1.06rem; margin-bottom: 8px; }
        .acc-tool__desc { color: #64748b; font-size: .91rem; line-height: 1.7; margin-bottom: 16px; }
        .acc-tool__more {
          margin-top: auto; display: inline-flex; align-items: center; gap: 8px;
          color: var(--peach-ink); font-weight: 700; font-size: .88rem;
        }
        .acc-tool__more svg { transition: transform .26s ease; }

        /* A mouse hovers; a finger can't. On touch, lib/touchMotion.js adds
           .is-live as the card scrolls through the middle of the screen. */
        .acc-tool:hover,
        .acc-tool.is-live {
          transform: translateY(-7px);
          box-shadow: 0 22px 46px rgba(194,65,12,.18);
          border-color: var(--peach-soft);
        }
        .acc-tool:hover::before,
        .acc-tool.is-live::before { transform: scaleX(1); }
        .acc-tool:hover .acc-tool__icon,
        .acc-tool.is-live .acc-tool__icon {
          background: linear-gradient(135deg, var(--peach-deep), var(--peach));
          color: #fff; transform: scale(1.06);
        }
        .acc-tool:hover .acc-tool__more svg,
        .acc-tool.is-live .acc-tool__more svg { transform: translateX(5px); }

        .acc-note {
          display: flex; align-items: flex-start; gap: 13px;
          margin-top: 28px; padding: 18px 20px; border-radius: 16px;
          background: #fff8f5; border: 1px solid #ffe0d1;
          color: #64748b; font-size: .92rem; line-height: 1.7;
        }
        .acc-note svg { color: var(--peach-deep); flex-shrink: 0; margin-top: 3px; }

        @media (prefers-reduced-motion: reduce) {
          .acc-tool, .acc-tool::before, .acc-tool__icon, .acc-logout { transition: none; }
          .acc-tool:hover, .acc-logout:hover { transform: none; }
        }
      `}</style>

      <div className="acc">
        <section className="acc-head">
          <span className="acc-head__mesh" />
          <Container style={{ maxWidth: "900px", position: "relative", zIndex: 1 }}>
            <div className="acc-profile">
              <span className="acc-avatar">{initials(user?.name)}</span>

              <div>
                <h1 className="acc-name">{user?.name}</h1>
                <p className="acc-email">{user?.email}</p>

                <div className="acc-chips">
                  <span className="acc-chip acc-chip--role">
                    <FaShieldAlt />
                    {isAdmin ? "ADMINISTRATOR" : "MEMBER"}
                  </span>

                  {/* Only rendered once the API actually returns createdAt */}
                  {user?.createdAt && (
                    <span className="acc-chip acc-chip--meta">
                      <FaCalendarAlt />
                      Member since{" "}
                      {new Date(user.createdAt).toLocaleDateString(undefined, {
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Container>
        </section>

        <section className="acc-body">
          <Container style={{ maxWidth: "900px", minHeight: "38vh" }}>
            <div className="acc-bar">
              <h2 className="acc-h2">
                {isAdmin ? "Admin Dashboard" : "My Account"}
              </h2>

              <button type="button" onClick={handleLogout} className="acc-logout">
                <FaSignOutAlt />
                Logout
              </button>
            </div>

            <Row className="g-4">
              {tools.map((tool) => (
                <Col md={6} lg={4} key={tool.to}>
                  <ToolCard {...tool} />
                </Col>
              ))}
            </Row>

            {!isAdmin && (
              <div className="acc-note">
                <FaInfoCircle />
                <span>
                  You're signed in as a member. Admin access is granted by an
                  existing administrator.
                </span>
              </div>
            )}
          </Container>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default Account;
